import { SelectedMenu, SelectedMenuProductList } from 'rangeConfig/@types';
import addToTarget from 'rangeConfig/shared/behaviors/addToTarget';
import addProduct from 'rangeConfig/shared/behaviors/addProduct';
import { CoreApi, ProductType } from '@inter-ikea-storageone/range';
import hideDoors from 'rangeConfig/shared/behaviors/hideDoors';
import addPlatsaInteriorSuggestion from 'rangeConfig/platsa/behaviors/addPlatsaInteriorSuggestion';
import TopPanelWidgetSelectedMenu from 'rangeConfig/shared/widgets/topPanels/TopPanelWidgetSelectedMenu';
import replaceProduct from 'rangeConfig/shared/behaviors/replaceProduct';
import { hideTopPanelsMenu } from 'rangeConfig/shared/rules/hideMenuRules';
import deleteFrame from 'rangeConfig/platsa/behaviors/deleteFrame';
import { noneOptionForInteriorSuggestions } from 'rangeConfig/shared/widgets/interiorSuggestions/noneOptionForInteriorSuggestions';
import {
  matchDimensions,
  matchFrameFrontType,
  matchWidth
} from 'rangeConfig/shared/filters/selectedMenuProductFilters';
import {
  filterSuggestionsForSlidingDoors,
  frameCanHaveInteriorSuggestion,
  matchInteriorDepth,
  matchInteriorSuggestionToFramePlacement
} from 'rangeConfig/platsa/filters/selectedMenuProductFilters';
import {
  isLightOnFrame,
  isNotOpenFrame,
  isOpenFrame
} from 'rangeConfig/platsa/rules/hideMenuRules';
import platform from 'rangeConfig/shared/utils/platform';
import DimensionsLabel from 'rangeConfig/shared/widgets/DimensionsLabel';
import showDoors from 'rangeConfig/shared/behaviors/showDoors';
import addFrontStyleToFrame from 'rangeConfig/shared/behaviors/addFrontStyleToFrame';
import { getLastQPPImage, getQPPImage } from 'products';
import dragAndDropLight from 'rangeConfig/platsa/behaviors/dragAndDropLight';
import addLightToTarget from 'rangeConfig/platsa/behaviors/addLightToTarget';
import removeInnerLightsFromProduct from 'rangeConfig/platsa/behaviors/removeInnerLightsFromProduct';
import { noProductsWithTypesForSelectedEntity } from 'rangeConfig/shared/rules/noneOptionSelectedRules';
import showClothHangersSheet from 'rangeConfig/shared/behaviors/showClothHangersSheet';

const frontStyle: SelectedMenuProductList = {
  id: 'frames-selected-menu-front-style',
  title: 'storageone.frontStyleTabTitle',
  cardsTitle: 'name',
  cardsSubtitle: 'designText',
  cluster: ['name', 'design'],
  productCardMainImage: getLastQPPImage,
  productCardHoverImage: getQPPImage,
  hidden: (coreApi: CoreApi, selectedEntityId: number) => isOpenFrame(coreApi, selectedEntityId),
  filters: [],
  behaviors: {
    productDropped: [showDoors, addFrontStyleToFrame],
    productClicked: [showDoors, addFrontStyleToFrame]
  },
  productTypes: [
    ProductType.PLATSA_HINGE_DOOR_ASM,
    ProductType.PLATSA_DRAWER_ASM,
    ProductType.PLATSA_SC_HINGE_DOOR_ASM
  ],
  productFilters: [matchFrameFrontType],
  sortOrder: [{ path: 'communicatedDimensions.height', reverse: true }]
};

const interiorSuggestions: SelectedMenuProductList = {
  id: 'frames-selected-menu-interior-suggestions',
  title: 'storageone.selectedMenuInteriorsCombinationsMenuTitle',
  productThumbToolTip: 'name',
  cardsTitle: '',
  cardsSubtitle: '',
  behaviors: {
    productClicked: [hideDoors, addPlatsaInteriorSuggestion],
    productDropped: [hideDoors, addPlatsaInteriorSuggestion]
  },
  productTypes: [ProductType.PLATSA_INTERIOR_SUGGESTION],
  productFilters: [
    frameCanHaveInteriorSuggestion,
    matchDimensions,
    filterSuggestionsForSlidingDoors,
    matchInteriorSuggestionToFramePlacement
  ],
  noneOption: noneOptionForInteriorSuggestions
};

const widgets = {
  desktop: [
    {
      id: 'display-size',
      title: 'storageone.sizeFilterLabel',
      component: DimensionsLabel
    },
    {
      id: 'frames-selected-menu-frames',
      title: 'storageone.productListFramesTitle',
      behaviors: {
        productClicked: [replaceProduct],
        productDropped: [replaceProduct]
      },
      hidden: isNotOpenFrame,
      noThumbs: () => true,
      productFilters: [matchDimensions]
    },
    {
      id: 'frames-selected-menu-top-panel',
      component: TopPanelWidgetSelectedMenu,
      title: 'storageone.productListTopPanelsTitle',
      hidden: hideTopPanelsMenu
    },
    frontStyle,
    interiorSuggestions,
    {
      id: 'frames-selected-menu-interiors',
      title: 'storageone.productListInteriorsTitle',
      productThumbToolTip: 'typeName, designText',
      behaviors: {
        productClicked: [hideDoors, addToTarget],
        productDropped: [hideDoors, addProduct]
      },
      productTypes: [
        ProductType.PLATSA_SHELF,
        ProductType.PLATSA_CLOTH_RAIL_40,
        ProductType.PLATSA_CLOTH_RAIL_55,
        ProductType.PLATSA_MULTIUSE_HANGER,
        ProductType.PLATSA_WIRE_BASKET_ASM,
        ProductType.PLATSA_SHOE_SHELF
      ],
      hidden: isOpenFrame,
      productFilters: [matchWidth, matchInteriorDepth]
    },
    {
      id: 'frames-selected-menu-lights',
      title: 'storageone.productListLightsTitle',
      productTypes: [ProductType.PLATSA_INNER_LIGHT],
      behaviors: {
        productClicked: [hideDoors, addLightToTarget],
        productDropped: [hideDoors, dragAndDropLight]
      },
      hidden: isOpenFrame,
      productFilters: [matchWidth],
      selected: isLightOnFrame,
      noneOption: {
        title: 'storageone.productListNoInnerLightsOptionTitle',
        subtitle: 'storageone.productListNoInnerLightsOptionSubtitle',
        behaviors: { removeProducts: [removeInnerLightsFromProduct] },
        selected: noProductsWithTypesForSelectedEntity([ProductType.PLATSA_INNER_LIGHT], true)
      }
    }
  ],
  mobile: [
    {
      id: 'frames-selected-menu-frames',
      title: 'storageone.productListFramesTitle',
      behaviors: {
        productClicked: [replaceProduct],
        productDropped: [replaceProduct]
      },
      hidden: isNotOpenFrame,
      noThumbs: () => true,
      productFilters: [matchDimensions]
    },
    {
      id: 'frames-selected-menu-top-panel',
      component: TopPanelWidgetSelectedMenu,
      title: 'storageone.productListTopPanelsTitle',
      hidden: hideTopPanelsMenu
    },
    frontStyle,
    interiorSuggestions
  ]
};

const selectedMenu: SelectedMenu = {
  menuTitle: 'storageone.selectedProductListCustomiseFrame',
  behaviors: {
    productDeleted: [deleteFrame],
    selectedMenuExited: [showClothHangersSheet]
  },
  content: widgets[platform]
};

export default selectedMenu;
